import React, { useCallback, useRef, useState } from 'react';
import type ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import type {
  PublicScanReportResponse,
  VisibilityReportBrandPresence,
  VisibilityReportContextGap,
  VisibilityReportFunnel,
  VisibilityReportTopicShare,
  VisibilityReportTrendPoint,
} from '@brand24-original/llm-types';
import { ChatbeatPdfService } from '../../../services/pdf';
import { SIGNUP } from '../../../routes/path';
import { getChartThemeColors, getFirstSeriesPalette } from '../../../utils/chartTheme';
import { SELECTED_BRAND_COLOR } from '../../../utils/utils';
import { brandLabel } from '../brandLabel';
import { formatRank } from '../formatRank';
import { formatLongDate } from '../../../helpers/date/formatLongDate';
import { modelLabel } from '../modelLabel';
import ReportPdfDocument, { ReportPdfLabels } from './ReportPdfDocument';
import {
  formatPeriodLabel,
  periodLocaleFor,
} from '../../../helpers/filters/formatPeriodLabel';

/**
 * Buduje PDF raportu i oddaje go jako plik. Dokument jest wektorowy (żywy tekst, ostre krawędzie),
 * więc napisy muszą przyjść tu z zewnątrz — react-pdf renderuje poza drzewem Reacta i nie widzi
 * kontekstu i18n.
 */
export const useReportPdfDownload = (
  report: PublicScanReportResponse | undefined,
  options?: {
    contextGaps?: VisibilityReportContextGap[];
    responsesAnalyzed?: number;
    // Sekcje spoza kontraktu Checkera — pełny raport klienta dokłada je z własnego payloadu.
    trend?: VisibilityReportTrendPoint[];
    funnel?: VisibilityReportFunnel;
    topicShares?: VisibilityReportTopicShare[];
    brandPresence?: VisibilityReportBrandPresence;
    /** Zakres raportu (`yyyy-mm-dd`); Checker go nie ma i zostaje przy dacie wygenerowania. */
    period?: { from: string; to: string };
  }
) => {
  const { t, i18n } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const rankingChartRef = useRef<ReactECharts>(null);
  const sovChartRef = useRef<ReactECharts>(null);
  const trendChartRef = useRef<ReactECharts>(null);
  const topicShareChartRef = useRef<ReactECharts>(null);

  /**
   * Zrzut wykresu prosto z ECharts. Dokument jest wektorowy, ale te dwa wykresy wchodzą jako obraz —
   * odtwarzanie ich prymitywami react-pdf i tak nie dałoby tego samego, co widać na stronie.
   */
  const chartImage = (ref: React.RefObject<ReactECharts>): string | undefined => {
    try {
      return ref.current
        ?.getEchartsInstance()
        .getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
    } catch {
      // Wykres mógł się jeszcze nie wyrenderować — PDF poradzi sobie bez obrazu.
      return undefined;
    }
  };

  const download = useCallback(async () => {
    if (!report || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      // Pozycja badanej marki w rankingu widoczności — ten sam porządek, co na slajdzie na ekranie
      // (`ReportCompetitiveRanking`), żeby PDF nie podał innej liczby niż podgląd.
      const rankedBrands = [...report.brandVisibility].sort((a, b) => b.visibility - a.visibility);
      const trackedRank = rankedBrands.findIndex((entry) => entry.isTracked) + 1;

      const labels: ReportPdfLabels = {
        coverSubtitle: t('reportCoverSubtitle'),
        // Okładka pokazuje ZAKRES raportu, jeśli raport go ma — inaczej datę wygenerowania.
        // Ta sama reguła co `ReportCover` (`periodLabel ?? generatedLabel`) na ekranie.
        coverDate: options?.period
          ? formatPeriodLabel(
              options.period.from,
              options.period.to,
              periodLocaleFor(i18n.language)
            )
          : formatLongDate(report.generatedAt, i18n.language),
        prompts: t('prompts'),
        models: t('models'),
        allResponses: t('reportAllResponses'),
        summaryTitle: t('reportSummaryTitle'),
        summaryTitleAccent: t('reportSummaryTitleAccent'),
        summarySubtitle: t('reportSummarySubtitle', {
          brand: brandLabel(report.brand),
          visibility: report.score.visibility,
          share: report.score.shareOfVoice,
        }),
        visibility: t('visibility'),
        visibilityValue: `${report.score.visibility}%`,
        shareOfVoice: t('shareOfVoice'),
        brandScore: t('brandScore'),
        positionLabel: t('averagePosition'),
        positionValue:
          report.score.averagePosition === null ? '-' : `#${report.score.averagePosition}`,
        platformTitle: t('reportPlatformTitle'),
        platformTitleAccent: t('reportPlatformTitleAccent'),
        platformDominant: t('reportPlatformDominant'),
        platformWeakest: t('reportPlatformWeakest'),
        platformEven: t('reportPlatformEven'),
        platformEvenDesc: (count: number) => t('reportPlatformEvenDesc', { count }),
        platformShare: (value: number) => t('reportPlatformShare', { value }),
        platformMetricsTitle: t('reportPlatformMetricsTitle'),
        platformMetricsDesc: t('reportPlatformMetricsDesc'),
        rankingTitle: t('reportRankingTitle', {
          rank: formatRank(trackedRank, i18n.language),
        }),
        rankingTitleAccent: t('reportRankingTitleAccent'),
        rankingSubtitle: t('reportRankingSubtitle', { brand: brandLabel(report.brand) }),
        sovTitle: t('reportSovTitle'),
        sovTitleAccent: t('reportSovTitleAccent'),
        sovChartTitle: t('reportSovChartTitle'),
        sovChartDesc: t('reportSovChartDesc'),
        matrixTitle: t('reportMatrixTitle'),
        matrixTitleAccent: t('reportMatrixTitleAccent'),
        matrixSubtitle: t('reportMatrixSubtitle'),
        gapsTitle: t('reportGapsTitle'),
        gapsTitleAccent: t('reportGapsTitleAccent'),
        gapsSubtitle: t('reportGapsSubtitle'),
        gapsTopic: t('topic'),
        gapsBrands: t('topBrands'),
        gapsOccurrences: t('occurrences'),
        domainsTitle: t('reportDomainsTitle'),
        domainsTitleAccent: t('reportDomainsTitleAccent'),
        domainsSubtitle: t('reportDomainsSubtitle'),
        domainsShare: (value: number) => t('reportDomainsShare', { value }),
        pagesTitle: t('reportPagesTitle'),
        pagesTitleAccent: t('reportPagesTitleAccent'),
        pagesSubtitle: t('reportPagesSubtitle'),
        promptsTitle: t('reportPromptsTitle'),
        promptsTitleAccent: t('reportPromptsTitleAccent'),
        promptsSubtitle: t('reportPromptsSubtitle'),
        promptsTopTitle: t('reportPromptsTopTitle'),
        promptsTopDesc: t('reportPromptsTopDesc'),
        promptsTopTitleGroups: t('reportPromptsTopTitleGroups'),
        promptsTopDescGroups: t('reportPromptsTopDescGroups'),
        promptsGroupTag: (count: number) => `${t('reportPromptsGroupTag')} ${count}`,
        promptsGroupTagCount: (count: number) => t('reportPromptsGroupTagCount', { count }),
        promptsLowest: t('reportPromptsLowest'),
        promptsBest: t('reportPromptsBest'),
        promptsWorst: t('reportPromptsWorst'),
        promptsEven: t('reportPromptsEven'),
        promptsEvenDesc: (count: number) => t('reportPromptsEvenDesc', { count }),
        brand: t('brand'),
        // Legenda trendu: `LineChart` rysuje ją w DOM-ie, więc zrzut ECharts jej NIE zawiera —
        // w PDF-ie musimy ją odtworzyć. Kolory biorą się z tych samych helperów, co serie wykresu
        // (patrz `BrandPositionChart`), żeby kropki zgadzały się z liniami.
        trendLegend: [
          { label: t('brandScore'), color: getFirstSeriesPalette()[3] },
          { label: t('visibility'), color: SELECTED_BRAND_COLOR },
          { label: t('averagePosition'), color: getChartThemeColors().neutralLine },
        ],
        trendTitle: t('reportTrendTitle'),
        trendTitleAccent: t('reportTrendVisibilityAccent'),
        trendSubtitle: t('reportTrendSubtitle'),
        funnelTitle: t('reportFunnelTitle'),
        funnelTitleAccent: t('reportFunnelTitleAccent'),
        funnelSubtitle: t('reportFunnelSubtitle'),
        funnelMentioned: t('reportFunnelMentioned'),
        funnelTop3: t('reportFunnelTop3'),
        funnelTop1: t('reportFunnelTop1'),
        funnelMentionedDesc: (responses: string, total: string) =>
          t('reportFunnelMentionedDesc', { responses, total, brand: brandLabel(report.brand) }),
        funnelTop3Desc: (responses: string) => t('reportFunnelTop3Desc', { responses }),
        funnelTop1Desc: (responses: string) =>
          t('reportFunnelTop1Desc', { responses, brand: brandLabel(report.brand) }),
        funnelStepTop3: t('reportFunnelStepTop3'),
        funnelStepTop3Desc: (value: number) => t('reportFunnelStepTop3Desc', { value }),
        funnelStepTop1: t('reportFunnelStepTop1'),
        funnelStepTop1Desc: (value: number) => t('reportFunnelStepTop1Desc', { value }),
        topicsTitle: t('reportTopicsTitle'),
        topicsTitleAccent: t('reportTopicsTitleAccent'),
        topicsSubtitle: t('reportTopicsSubtitle'),
        presenceTitle: t('reportPresenceTitle'),
        presenceTitleAccent: t('reportPresenceTitleAccent'),
        presenceSubtitle: t('reportPresenceSubtitle'),
        rankingYourBrand: t('reportRankingYourBrand'),
        numberFormat: (value: number) => new Intl.NumberFormat(i18n.language).format(value),
        promptsLockedTitle: t('reportPromptsLockedTitle'),
        promptsLockedDesc: t('reportPromptsLockedDesc'),
        promptsLockedCta: t('reportPromptsLockedCta'),
        // PDF żyje poza przeglądarką, więc link musi być bezwzględny.
        signUpUrl: `${window.location.origin}${SIGNUP}`,
      };

      await ChatbeatPdfService.generate(
        <ReportPdfDocument
          report={report}
          labels={labels}
          contextGaps={options?.contextGaps}
          responsesAnalyzed={options?.responsesAnalyzed}
          funnel={options?.funnel}
          topicShares={options?.topicShares}
          brandPresence={options?.brandPresence}
          modelName={modelLabel}
          rankingChart={chartImage(rankingChartRef)}
          sovChart={chartImage(sovChartRef)}
          trendChart={chartImage(trendChartRef)}
          topicShareChart={chartImage(topicShareChartRef)}
        />,
        // Data w nazwie: przy stałej nazwie przeglądarka dopisuje „(1)", a z historii pobierania
        // łatwo otworzyć starszy plik i wziąć go za świeży eksport.
        `chatbeat-visibility-report-${report.domain}-${report.generatedAt.slice(0, 10)}.pdf`
      );
    } finally {
      setIsDownloading(false);
    }
  }, [
    report,
    isDownloading,
    t,
    i18n.language,
    options?.contextGaps,
    options?.responsesAnalyzed,
    options?.funnel,
    options?.topicShares,
    options?.brandPresence,
  ]);

  return {
    download,
    isDownloading,
    rankingChartRef,
    sovChartRef,
    trendChartRef,
    topicShareChartRef,
  };
};
